import { cx } from '@emotion/css';
import { ArrowBack } from '@mui/icons-material';
import { Avatar, Button, Checkbox, Divider, FormControl } from '@sortlist-frontend/design-system/legacy';
import { ImageSourceHelper } from '@sortlist-frontend/media';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { isAutomation } from '@sortlist-frontend/utils';
import { FC, Fragment, useRef, useState } from 'react';

import { usePublicAppContext } from '_core/context/public-app-context';
import { Action, useCloudflareTurnstile } from '_core/hooks/useCloudflareTurnstile';
import { Agency } from '_core/repos/agency.repo';
import { agencyClaimConfig } from '_features/agency/claim/agency-claim.config';

import * as S from './styles';

type AgencyClaimStatus = 'CLAIMED' | 'UNCLAIMED' | 'NEW_RECORD';
type AgencyClaim = Partial<Agency> & { status: AgencyClaimStatus };

type ClaimConfirmationProps = {
  agency: AgencyClaim;
  onConfirmedAgency: (confirmedAgency: AgencyClaim) => void;
};

export const ClaimConfirmation: FC<ClaimConfirmationProps> = (props) => {
  const { agency, onConfirmedAgency } = props;
  const { getUrl } = usePublicAppContext();
  const [confirmAuthority, setConfirmAuthority] = useState<boolean>(false);
  const { t } = useTranslation(agencyClaimConfig.i18nNamespaces);

  const warnings: Record<typeof agency.status, string> = {
    UNCLAIMED: t('agency:claim.confirmation.status.unclaimed_V2'),
    CLAIMED: t('agency:claim.confirmation.status.claimed_V2'),
    NEW_RECORD: '',
  };

  const ref = useRef<HTMLDivElement>(null);
  const { isSuccess: isTurnstileVerified } = useCloudflareTurnstile({
    ref,
    action: Action.CREATE_PROFILE,
    disabled: isAutomation(),
  });

  return (
    <div className="layout-column layout-align-start-center">
      <S.Card className="layout-column layout-align-start-stretch overflow-hidden shadow-2 rounded-lg bg-neutral-50 border border-secondary-300">
        {!!warnings[agency.status] && (
          <header
            className={cx(
              agency.status === 'CLAIMED' ? 'bg-accent-100' : 'bg-primary-100',
              'flex-1 p-4 text-center layout-align-center-center layout-row',
            )}>
            <small className={cx(agency.status === 'CLAIMED' ? 'text-accent-700' : 'text-primary-500')}>
              {warnings[agency.status]}
            </small>
          </header>
        )}
        <div className="px-8 py-16 layout-column  layout-align-center-center">
          <Avatar
            className="display-block"
            shape="circle"
            size="lg"
            fit="contain"
            src={agency.logo_url || ImageSourceHelper.fromPath('/_img/default/default-image-square.svg')}
            alt={`${agency.slug}-logo`}
          />
          <p className="mt-2 h6 bold">{agency.name}</p>
          {!!agency.website && <small className="mt-2 text-truncate display-block">{agency.website}</small>}
        </div>
        <Divider />
        <footer className="py-8 px-4 layout-column layout-align-start-stretch">
          {agency.status === 'CLAIMED' ? (
            <Fragment>
              <span>{t('agency:claim.confirmation.invite')}</span>
              <a
                className="btn btn-raised btn-primary btn-md mt-8 display-block"
                href={`https://sortlist1.typeform.com/to/A2or9YKB#agencyslug=${agency.slug}`}>
                {t('agency:claim.confirmation.contactSupport')}
              </a>
            </Fragment>
          ) : (
            <Fragment>
              <FormControl
                control={
                  <Checkbox
                    id="confirm_authority"
                    name="confirm_authority"
                    checked={confirmAuthority}
                    onChange={() => {
                      setConfirmAuthority(!confirmAuthority);
                    }}
                    color="primary-500"
                  />
                }
                label={
                  <small>
                    {agency.status === 'NEW_RECORD'
                      ? t('agency:claim.landing.confirmCreation_V2')
                      : t('agency:claim.landing.confirmAuthority_V2')}
                  </small>
                }
              />
              <div className="mt-4" ref={ref} />
              <Button
                disabled={!confirmAuthority || !isTurnstileVerified}
                buttonStyle={'primary'}
                buttonVariant={'raised'}
                size={'md'}
                className="mt-4"
                label={
                  agency.status === 'NEW_RECORD'
                    ? t('agency:claim.confirmation.btnLabelCreate_V2')
                    : t('agency:claim.confirmation.btnLabelClaim_V2')
                }
                id={agency.status === 'NEW_RECORD' ? 'claim-create-btn' : 'claim-btn'}
                onClick={(e) => onConfirmedAgency(agency)}
              />
            </Fragment>
          )}
        </footer>
      </S.Card>
      <a
        href={getUrl('agency.claim.landing')}
        className="mt-4 underline cursor-pointer text-primary-500 layout-row layout-align-center-center">
        <ArrowBack className="mr-1" fontSize="inherit" style={{ verticalAlign: 'middle' }} />
        <span className="small">{t('agency:claim.confirmation.other_V2')}</span>
      </a>
    </div>
  );
};
